/**
 * GYA module — multi-segment, geo-distributed services view.
 *
 * Redesigned (iter9):
 *  - Sub-tab navigation (Segmentos · Partners · Destinos · Funnel) to break the
 *    long page into focused workspaces and give every field room to breathe.
 *  - Every field is editable. Partners support add/delete rows. Segments,
 *    journey stages and KPIs are edited in-place.
 *  - Country codes are now ES · PT · IT (Italia replaces Morocco/MA).
 */
import { useEffect, useMemo, useState } from "react";
import {
  FunnelChart,
  Funnel,
  Cell,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import {
  Star,
  Plus,
  Trash2,
  Users,
  MapPin,
  Globe2,
  TrendingUp,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import {
  SectionCard,
  FieldLabel,
  TextInput,
  TextArea,
  PrimaryButton,
  SecondaryButton,
  AIBadge,
  Overline,
} from "@/components/ui-kit";
import AIAssistant from "@/components/AIAssistant";

const empty = {
  segments: [],
  country_partners: {},
  destinations: [],
  funnel_summary: {
    monthly_marketing_spend: 0,
    monthly_leads_total: 0,
    monthly_consultations: 0,
    monthly_proposals: 0,
    monthly_contracts: 0,
    monthly_revenue_new: 0,
    monthly_revenue_recurring: 0,
    blended_ltv: 0,
    blended_cac: 0,
    blended_ltv_cac: 0,
  },
};

const SEG_ACCENT = {
  retirees: { bg: "bg-[#F5EFE6]", border: "border-[#B89154]/40", text: "text-[#B89154]", bar: "bg-[#B89154]" },
  working_families: { bg: "bg-[#2563EB]/5", border: "border-[#2563EB]/30", text: "text-[#2563EB]", bar: "bg-[#2563EB]" },
};

const COUNTRY_NAMES = {
  ES: { es: "España", en: "Spain", flag: "🇪🇸" },
  PT: { es: "Portugal", en: "Portugal", flag: "🇵🇹" },
  IT: { es: "Italia", en: "Italy", flag: "🇮🇹" },
};

// --- helpers ---------------------------------------------------------------

function NumInput({ value, onChange, prefix, suffix, testId, step = 1 }) {
  return (
    <div className="flex items-center border border-[#E8E6E1] bg-white rounded-sm focus-within:ring-1 focus-within:ring-[#2563EB] focus-within:border-[#2563EB]">
      {prefix && <span className="px-3 text-xs text-[#8A8886] border-r border-[#E8E6E1]">{prefix}</span>}
      <input
        type="number"
        step={step}
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value === "" ? 0 : parseFloat(e.target.value))}
        data-testid={testId}
        className="w-full px-3 py-2 text-sm font-mono outline-none bg-transparent"
        style={{ fontFamily: '"IBM Plex Mono", monospace' }}
      />
      {suffix && <span className="px-3 text-xs text-[#8A8886] border-l border-[#E8E6E1]">{suffix}</span>}
    </div>
  );
}

function StarsInput({ value, onChange }) {
  const n = Math.max(0, Math.min(5, Math.round(value || 0)));
  return (
    <div className="inline-flex gap-0.5 text-[#B89154]">
      {Array.from({ length: 5 }).map((_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i + 1)}
          className="hover:scale-110 transition-transform"
          aria-label={`Rate ${i + 1}`}
        >
          <Star className="h-4 w-4" strokeWidth={1.5} fill={i < n ? "currentColor" : "none"} />
        </button>
      ))}
    </div>
  );
}

function SubTabBar({ tabs, active, onChange }) {
  return (
    <div className="flex flex-wrap border-b border-[#E8E6E1] -mt-2 mb-6" data-testid="gya-subtabs">
      {tabs.map((t, idx) => {
        const Icon = t.icon;
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={`relative flex items-center gap-2 px-5 py-3 text-sm transition-all border-b-2 ${
              isActive
                ? "border-[#2563EB] text-[#2563EB] font-medium"
                : "border-transparent text-[#5C5B5A] hover:text-[#1A1A1A]"
            }`}
            data-testid={`gya-subtab-${t.key}`}
          >
            <span
              className="font-mono text-[10px] text-[#8A8886]"
              style={{ fontFamily: '"IBM Plex Mono", monospace' }}
            >
              {String(idx + 1).padStart(2, "0")}
            </span>
            <Icon className="h-4 w-4" strokeWidth={1.5} />
            <span>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// --- Segments editor -------------------------------------------------------

function SegmentEditor({ segment, onChange, lang, t }) {
  const accent = SEG_ACCENT[segment.key] || SEG_ACCENT.retirees;
  const persona = segment.persona || {};
  const kpis = segment.kpis || {};
  const journey = segment.journey_stages || [];

  function setField(path, value) {
    const next = { ...segment };
    if (path.length === 1) {
      next[path[0]] = value;
    } else if (path.length === 2) {
      next[path[0]] = { ...(segment[path[0]] || {}), [path[1]]: value };
    }
    onChange(next);
  }
  function setJourney(idx, key, value) {
    const next = { ...segment, journey_stages: [...journey] };
    next.journey_stages[idx] = { ...journey[idx], [key]: value };
    onChange(next);
  }

  return (
    <article
      className={`border ${accent.border} rounded-sm bg-white flex flex-col`}
      data-testid={`gya-segment-${segment.key}`}
    >
      <header className={`${accent.bg} px-6 py-4 border-b ${accent.border}`}>
        <Overline><span className={accent.text}>{lang === "es" ? "Segmento" : "Segment"}</span></Overline>
        <div className="mt-2 grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-3">
          <TextInput
            value={segment.name}
            onChange={(v) => setField(["name"], v)}
            placeholder={lang === "es" ? "Nombre del segmento" : "Segment name"}
            testId={`gya-seg-name-${segment.key}`}
          />
          <TextInput
            value={segment.tagline || ""}
            onChange={(v) => setField(["tagline"], v)}
            placeholder={lang === "es" ? "Tagline" : "Tagline"}
            testId={`gya-seg-tagline-${segment.key}`}
          />
        </div>
      </header>

      {/* PERSONA */}
      <div className="p-6 space-y-4">
        <Overline>{t("gya.segment.persona")}</Overline>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <FieldLabel>{lang === "es" ? "Edad" : "Age"}</FieldLabel>
            <TextInput value={persona.age || ""} onChange={(v) => setField(["persona", "age"], v)} placeholder="65-75" />
          </div>
          <div>
            <FieldLabel>{t("gya.segment.income")}</FieldLabel>
            <TextInput value={persona.income || ""} onChange={(v) => setField(["persona", "income"], v)} placeholder="$80k-$150k" />
          </div>
        </div>
        <div>
          <FieldLabel>{t("gya.segment.motivation")}</FieldLabel>
          <TextArea rows={2} value={persona.motivation || ""} onChange={(v) => setField(["persona", "motivation"], v)} />
        </div>
        <div>
          <FieldLabel>{t("gya.segment.pains")}</FieldLabel>
          <TextArea rows={2} value={persona.pains || ""} onChange={(v) => setField(["persona", "pains"], v)} />
        </div>
        <div>
          <FieldLabel>{t("gya.segment.drivers")}</FieldLabel>
          <TextArea rows={2} value={persona.decision_drivers || ""} onChange={(v) => setField(["persona", "decision_drivers"], v)} />
        </div>
      </div>

      {/* JOURNEY */}
      <div className="px-6 py-5 border-t border-[#F0EFE9]">
        <Overline>{t("gya.segment.journey")}</Overline>
        <p className="text-xs text-[#8A8886] mt-1 mb-4">{lang === "es" ? "Tasa de conversión por etapa (%)" : "Conversion rate by stage (%)"}</p>
        <div className="space-y-2">
          {journey.map((s, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_70px] gap-2 items-center" data-testid={`gya-journey-${segment.key}-${i}`}>
              <TextInput value={s.stage || ""} onChange={(v) => setJourney(i, "stage", v)} placeholder={lang === "es" ? "Etapa" : "Stage"} />
              <div className="relative h-9 bg-[#F5F4F0] rounded-sm overflow-hidden">
                <div className={`absolute inset-y-0 left-0 ${accent.bar} transition-all`} style={{ width: `${Math.min(100, s.conversion_pct || 0)}%` }} />
                <input
                  type="text"
                  value={s.channels || ""}
                  onChange={(e) => setJourney(i, "channels", e.target.value)}
                  className="relative w-full h-full bg-transparent px-2 text-xs text-[#1A1A1A] outline-none"
                  placeholder={lang === "es" ? "Canales" : "Channels"}
                />
              </div>
              <NumInput value={s.conversion_pct || 0} onChange={(v) => setJourney(i, "conversion_pct", v)} suffix="%" />
            </div>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div className="px-6 py-5 border-t border-[#F0EFE9]">
        <Overline>{t("gya.segment.kpis")}</Overline>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <FieldLabel>{t("gya.kpi.avg_ticket_initial")}</FieldLabel>
            <NumInput prefix="€" value={kpis.avg_ticket_initial || 0} onChange={(v) => setField(["kpis", "avg_ticket_initial"], v)} step={100} />
          </div>
          <div>
            <FieldLabel>{t("gya.kpi.avg_ticket_recurring")}</FieldLabel>
            <NumInput prefix="€" suffix="/yr" value={kpis.avg_ticket_recurring || 0} onChange={(v) => setField(["kpis", "avg_ticket_recurring"], v)} step={100} />
          </div>
          <div>
            <FieldLabel>{t("gya.kpi.ltv_3yr")}</FieldLabel>
            <NumInput prefix="€" value={kpis.ltv_3yr || 0} onChange={(v) => setField(["kpis", "ltv_3yr"], v)} step={500} />
          </div>
          <div>
            <FieldLabel>{t("gya.kpi.ltv_5yr")}</FieldLabel>
            <NumInput prefix="€" value={kpis.ltv_5yr || 0} onChange={(v) => setField(["kpis", "ltv_5yr"], v)} step={500} />
          </div>
          <div>
            <FieldLabel>{t("gya.kpi.cac_target")}</FieldLabel>
            <NumInput prefix="€" value={kpis.cac_target || 0} onChange={(v) => setField(["kpis", "cac_target"], v)} step={50} />
          </div>
          <div>
            <FieldLabel>{t("gya.kpi.ltv_cac")}</FieldLabel>
            <NumInput value={kpis.ltv_cac_ratio || 0} onChange={(v) => setField(["kpis", "ltv_cac_ratio"], v)} suffix="×" step={0.1} />
          </div>
          <div>
            <FieldLabel>{t("gya.kpi.payback")}</FieldLabel>
            <NumInput value={kpis.payback_months || 0} onChange={(v) => setField(["kpis", "payback_months"], v)} suffix="mo" />
          </div>
          <div>
            <FieldLabel>{t("gya.kpi.monthly_lead")} → {t("gya.kpi.monthly_customers")}</FieldLabel>
            <div className="grid grid-cols-2 gap-2">
              <NumInput value={kpis.monthly_lead_target || 0} onChange={(v) => setField(["kpis", "monthly_lead_target"], v)} />
              <NumInput value={kpis.monthly_customers || 0} onChange={(v) => setField(["kpis", "monthly_customers"], v)} />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

// --- Partners editor -------------------------------------------------------

function PartnersByCountry({ countryPartners, onChange, lang, t }) {
  const codes = Object.keys(countryPartners).length > 0 ? Object.keys(countryPartners) : ["ES", "PT", "IT"];
  const [activeCode, setActiveCode] = useState(codes[0]);
  const list = countryPartners[activeCode] || [];

  function setList(next) {
    onChange({ ...countryPartners, [activeCode]: next });
  }
  function addRow() {
    setList([...list, { category: "", name: "", city: "", speciality: "", contract_type: "", rating: 4 }]);
  }
  function delRow(idx) {
    setList(list.filter((_, i) => i !== idx));
  }
  function setRow(idx, key, value) {
    const next = list.map((r, i) => (i === idx ? { ...r, [key]: value } : r));
    setList(next);
  }

  return (
    <div>
      {/* Country pills */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="gya-country-pills">
        {codes.map((code) => {
          const info = COUNTRY_NAMES[code] || { es: code, en: code, flag: "" };
          const isActive = code === activeCode;
          const count = (countryPartners[code] || []).length;
          return (
            <button
              key={code}
              type="button"
              onClick={() => setActiveCode(code)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm border text-sm transition-all ${
                isActive
                  ? "border-[#2563EB] bg-[#2563EB] text-white"
                  : "border-[#E8E6E1] bg-white text-[#1A1A1A] hover:border-[#2563EB]"
              }`}
              data-testid={`gya-country-pill-${code}`}
            >
              <span className="text-base">{info.flag}</span>
              <span>{info[lang]}</span>
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${isActive ? "bg-white/15 text-white" : "bg-[#F5F4F0] text-[#5C5B5A]"}`} style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="border border-[#E8E6E1] rounded-sm bg-white overflow-x-auto" data-testid={`gya-partners-table-${activeCode}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-[#1A1A1A] text-left bg-[#F5F4F0]">
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wide text-[#5C5B5A] w-32">{t("gya.partners.category")}</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wide text-[#5C5B5A]">{t("gya.partners.name")}</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wide text-[#5C5B5A] w-36">{t("gya.partners.city")}</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wide text-[#5C5B5A]">{t("gya.partners.speciality")}</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wide text-[#5C5B5A] w-44">{t("gya.partners.contract")}</th>
              <th className="px-3 py-2.5 text-[10px] uppercase tracking-wide text-[#5C5B5A] w-28">{t("gya.partners.rating")}</th>
              <th className="px-2 py-2.5 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((p, i) => (
              <tr key={i} className="border-b border-[#F0EFE9] hover:bg-[#FBFAF7]" data-testid={`gya-partner-row-${activeCode}-${i}`}>
                <td className="px-2 py-1.5"><TextInput value={p.category} onChange={(v) => setRow(i, "category", v)} placeholder="Legal" /></td>
                <td className="px-2 py-1.5"><TextInput value={p.name} onChange={(v) => setRow(i, "name", v)} /></td>
                <td className="px-2 py-1.5"><TextInput value={p.city} onChange={(v) => setRow(i, "city", v)} /></td>
                <td className="px-2 py-1.5"><TextInput value={p.speciality} onChange={(v) => setRow(i, "speciality", v)} /></td>
                <td className="px-2 py-1.5"><TextInput value={p.contract_type} onChange={(v) => setRow(i, "contract_type", v)} /></td>
                <td className="px-2 py-1.5"><StarsInput value={p.rating} onChange={(v) => setRow(i, "rating", v)} /></td>
                <td className="px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => delRow(i)}
                    className="text-[#8A8886] hover:text-[#9E2A2B] transition-colors"
                    aria-label="delete"
                    data-testid={`gya-del-partner-${activeCode}-${i}`}
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-[#8A8886]">
                {lang === "es" ? "Sin partners. Añade el primero." : "No partners. Add the first one."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex justify-end">
        <SecondaryButton onClick={addRow} testId={`gya-add-partner-${activeCode}`}>
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          {lang === "es" ? "Añadir partner" : "Add partner"}
        </SecondaryButton>
      </div>
    </div>
  );
}

// --- Destinations editor ---------------------------------------------------

function DestinationCardEditor({ destination, onChange, t, lang }) {
  function setField(key, value) {
    onChange({ ...destination, [key]: value });
  }
  const fields = [
    { key: "visa_options_retirees", labelK: "gya.dest.visa_retirees", accent: "text-[#B89154]", rows: 2 },
    { key: "visa_options_workers", labelK: "gya.dest.visa_workers", accent: "text-[#2563EB]", rows: 2 },
    { key: "fiscal_highlights", labelK: "gya.dest.fiscal", rows: 3 },
    { key: "cost_of_living_idx", labelK: "gya.dest.col", rows: 1 },
    { key: "healthcare", labelK: "gya.dest.healthcare", rows: 2 },
    { key: "schools_intl", labelK: "gya.dest.schools_intl", rows: 2 },
    { key: "schools_bilingual", labelK: "gya.dest.schools_bilingual", rows: 2 },
    { key: "expat_communities", labelK: "gya.dest.community", rows: 2 },
    { key: "best_for_retirees", labelK: "gya.dest.best_retirees", accent: "text-[#B89154]", rows: 1 },
    { key: "best_for_families", labelK: "gya.dest.best_families", accent: "text-[#2563EB]", rows: 1 },
  ];

  return (
    <article className="border border-[#E8E6E1] bg-white rounded-sm overflow-hidden" data-testid={`gya-destination-${destination.country_code}`}>
      <header className="px-6 py-5 border-b border-[#E8E6E1] bg-[#F5F4F0]">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-4xl">{destination.flag}</span>
          <div className="flex-1">
            <Overline>{destination.country_code}</Overline>
            <TextInput
              value={destination.country || ""}
              onChange={(v) => setField("country", v)}
              testId={`gya-dest-name-${destination.country_code}`}
            />
          </div>
        </div>
      </header>
      <div className="p-6 space-y-5">
        {fields.map((f) => (
          <div key={f.key}>
            <FieldLabel>
              <span className={f.accent}>{t(f.labelK)}</span>
            </FieldLabel>
            {f.rows > 1 ? (
              <TextArea value={destination[f.key] || ""} onChange={(v) => setField(f.key, v)} rows={f.rows} />
            ) : (
              <TextInput value={destination[f.key] || ""} onChange={(v) => setField(f.key, v)} />
            )}
          </div>
        ))}
      </div>
    </article>
  );
}

// --- Main component --------------------------------------------------------

export default function GYAModule({ data, project, onSave }) {
  const { t, lang } = useLang();
  const [v, setV] = useState({ ...empty, ...(data || {}) });
  const [tab, setTab] = useState("segments");
  useEffect(() => {
    setV((prev) => {
    const next = { ...empty, ...(data || {}) };
    try { if (JSON.stringify(prev) === JSON.stringify(next)) return prev; } catch (e) { void e; }
    return next;
  });
  }, [data]);
  useEffect(() => {
    onSave?.(v);
  }, [v, onSave]);

  const fs = v.funnel_summary || empty.funnel_summary;

  const funnelData = useMemo(
    () => [
      { name: t("gya.funnel.leads"), value: fs.monthly_leads_total || 0 },
      { name: t("gya.funnel.consultations"), value: fs.monthly_consultations || 0 },
      { name: t("gya.funnel.proposals"), value: fs.monthly_proposals || 0 },
      { name: t("gya.funnel.contracts"), value: fs.monthly_contracts || 0 },
    ],
    [fs.monthly_leads_total, fs.monthly_consultations, fs.monthly_proposals, fs.monthly_contracts, lang]
  );

  const suggestions = lang === "es"
    ? [
        "Sugiere 3 partners adicionales por país (legal, fiscal, escuela)",
        "Optimiza el funnel: ¿dónde estoy perdiendo más leads?",
        "Propón un tercer segmento (ej: estudiantes universitarios o nómadas digitales)",
        "Calcula el LTV/CAC ideal para añadir un nuevo destino (Grecia, Francia)",
      ]
    : [
        "Suggest 3 more partners per country (legal, fiscal, school)",
        "Optimize the funnel: where am I losing the most leads?",
        "Propose a third segment (e.g. university students or digital nomads)",
        "Compute ideal LTV/CAC to add a new destination (Greece, France)",
      ];

  const tabs = [
    { key: "segments", label: t("gya.segments_title"), icon: Users },
    { key: "partners", label: t("gya.partners_title"), icon: MapPin },
    { key: "destinations", label: t("gya.destinations_title"), icon: Globe2 },
    { key: "funnel", label: t("gya.funnel_title"), icon: TrendingUp },
  ];

  function updateSegment(i, next) {
    setV({ ...v, segments: v.segments.map((s, idx) => (idx === i ? next : s)) });
  }
  function updatePartners(next) {
    setV({ ...v, country_partners: next });
  }
  function updateDestination(i, next) {
    setV({ ...v, destinations: v.destinations.map((d, idx) => (idx === i ? next : d)) });
  }
  function setFunnel(key, value) {
    setV({ ...v, funnel_summary: { ...fs, [key]: value } });
  }

  return (
    <>
      {/* Header card with intro + AI assist + sub-tabs */}
      <SectionCard
        title={t("module.gya")}
        subtitle={t("gya.subtitle")}
        action={
          <div className="flex items-center gap-3">
            <AIBadge />
            <AIAssistant module="gya" context={{ project: project?.name, segments: (v.segments || []).map(s => s.name).join(", ") }} suggestions={suggestions} />
          </div>
        }
        testId="gya-header-section"
      >
        <p className="text-sm text-[#5C5B5A] leading-relaxed max-w-3xl mb-5">
          {lang === "es"
            ? "Diseñado para negocios con múltiples segmentos de cliente, distribución geográfica de partners y plantillas por destino. Navega por las 4 secciones para editar segmentos, mapa de partners, fichas de destino y funnel agregado."
            : "Designed for businesses with multiple customer segments, geo-distributed partner network and destination templates. Browse the 4 sections to edit segments, partner map, destination cards and aggregated funnel."}
        </p>

        <SubTabBar tabs={tabs} active={tab} onChange={setTab} />

        {/* Active tab indicator (auto-save handles persistence) */}
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#F0EFE9]" data-testid="gya-save-bar">
          <p className="text-xs text-[#8A8886]">
            {lang === "es" ? "Pestaña activa: " : "Active tab: "}
            <span className="text-[#2563EB] font-medium">
              {tabs.find((tb) => tb.key === tab)?.label}
            </span>
          </p>
        </div>

        {/* SEGMENTS */}
        {tab === "segments" && (
          <div data-testid="gya-tab-segments" className="space-y-6">
            {(v.segments || []).length === 0 ? (
              <div className="text-sm text-[#8A8886] py-10 text-center">—</div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-6">
                {v.segments.map((s, i) => (
                  <SegmentEditor key={s.key || i} segment={s} onChange={(n) => updateSegment(i, n)} lang={lang} t={t} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* PARTNERS */}
        {tab === "partners" && (
          <div data-testid="gya-tab-partners">
            <PartnersByCountry
              countryPartners={v.country_partners || {}}
              onChange={updatePartners}
              lang={lang}
              t={t}
            />
          </div>
        )}

        {/* DESTINATIONS */}
        {tab === "destinations" && (
          <div data-testid="gya-tab-destinations">
            <p className="text-sm text-[#8A8886] mb-5">
              {lang === "es"
                ? "Ficha técnica por destino. Edita las opciones de visado, fiscalidad, sanidad y escuelas que ofrecéis a cada segmento."
                : "Country fact-sheet. Edit visa options, taxation, healthcare and schools that you offer to each segment."}
            </p>
            <div className="grid lg:grid-cols-3 gap-5">
              {(v.destinations || []).map((d, i) => (
                <DestinationCardEditor key={d.country_code || i} destination={d} onChange={(n) => updateDestination(i, n)} t={t} lang={lang} />
              ))}
            </div>
          </div>
        )}

        {/* FUNNEL */}
        {tab === "funnel" && (
          <div data-testid="gya-tab-funnel" className="grid xl:grid-cols-[1fr_1.4fr] gap-8">
            {/* viz */}
            <div className="flex flex-col gap-4">
              <Overline>{lang === "es" ? "Visualización" : "Visualization"}</Overline>
              <div className="h-80 border border-[#E8E6E1] rounded-sm bg-[#FBFAF7] p-3">
                <ResponsiveContainer width="100%" height="100%">
                  <FunnelChart>
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E8E6E1", borderRadius: 2, fontFamily: "IBM Plex Mono, monospace", fontSize: 12 }} />
                    <Funnel dataKey="value" data={funnelData} isAnimationActive>
                      {funnelData.map((entry, i) => (
                        <Cell key={`cell-${i}`} fill={["#2563EB", "#B89154", "#3A5A6B", "#1A1A1A"][i % 4]} />
                      ))}
                      <LabelList position="right" fill="#1A1A1A" stroke="none" dataKey="name" />
                      <LabelList position="center" fill="#fff" stroke="none" dataKey="value" />
                    </Funnel>
                  </FunnelChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-[#8A8886] leading-relaxed">
                {lang === "es"
                  ? "Edita los valores en la columna de la derecha. El embudo se actualiza en tiempo real."
                  : "Edit the values on the right column. The funnel updates in real time."}
              </p>
            </div>

            {/* fields grid */}
            <div className="space-y-6">
              <div>
                <Overline>{lang === "es" ? "Volúmenes mensuales" : "Monthly volumes"}</Overline>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel>{t("gya.funnel.spend")}</FieldLabel>
                    <NumInput prefix="€" value={fs.monthly_marketing_spend || 0} onChange={(v) => setFunnel("monthly_marketing_spend", v)} step={100} testId="gya-funnel-spend" />
                  </div>
                  <div>
                    <FieldLabel>{t("gya.funnel.leads")}</FieldLabel>
                    <NumInput value={fs.monthly_leads_total || 0} onChange={(v) => setFunnel("monthly_leads_total", v)} testId="gya-funnel-leads" />
                  </div>
                  <div>
                    <FieldLabel>{t("gya.funnel.consultations")}</FieldLabel>
                    <NumInput value={fs.monthly_consultations || 0} onChange={(v) => setFunnel("monthly_consultations", v)} />
                  </div>
                  <div>
                    <FieldLabel>{t("gya.funnel.proposals")}</FieldLabel>
                    <NumInput value={fs.monthly_proposals || 0} onChange={(v) => setFunnel("monthly_proposals", v)} />
                  </div>
                  <div>
                    <FieldLabel>{t("gya.funnel.contracts")}</FieldLabel>
                    <NumInput value={fs.monthly_contracts || 0} onChange={(v) => setFunnel("monthly_contracts", v)} testId="gya-funnel-contracts" />
                  </div>
                </div>
              </div>

              <div>
                <Overline>{lang === "es" ? "Ingresos mensuales" : "Monthly revenue"}</Overline>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel>{t("gya.funnel.revenue_new")}</FieldLabel>
                    <NumInput prefix="€" value={fs.monthly_revenue_new || 0} onChange={(v) => setFunnel("monthly_revenue_new", v)} step={500} />
                  </div>
                  <div>
                    <FieldLabel>{t("gya.funnel.revenue_rec")}</FieldLabel>
                    <NumInput prefix="€" value={fs.monthly_revenue_recurring || 0} onChange={(v) => setFunnel("monthly_revenue_recurring", v)} step={500} />
                  </div>
                </div>
              </div>

              <div>
                <Overline>{lang === "es" ? "Métricas mezcladas" : "Blended metrics"}</Overline>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <div>
                    <FieldLabel>{t("gya.funnel.blended_ltv")}</FieldLabel>
                    <NumInput prefix="€" value={fs.blended_ltv || 0} onChange={(v) => setFunnel("blended_ltv", v)} step={500} />
                  </div>
                  <div>
                    <FieldLabel>{t("gya.funnel.blended_cac")}</FieldLabel>
                    <NumInput prefix="€" value={fs.blended_cac || 0} onChange={(v) => setFunnel("blended_cac", v)} step={50} />
                  </div>
                  <div>
                    <FieldLabel>{t("gya.funnel.blended_ratio")}</FieldLabel>
                    <NumInput value={fs.blended_ltv_cac || 0} onChange={(v) => setFunnel("blended_ltv_cac", v)} suffix="×" step={0.1} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </SectionCard>
    </>
  );
}
